/**
 * Wipe customer-scoped in-memory + AsyncStorage caches on logout / session invalidation.
 * React Query is cleared separately via `queryClient.clear()`.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useMenuStore as useClassicMenuStore } from '../../data/domains/menu/store';
import { useMenuStore as useFoodMenuStore } from '../../data/domains/foodMenu/store';
import {
  resetBootMenuPrefetchGate,
} from '../../data/domains/menuPrefetch';
import { clearSelectedSubscription } from '../../data/domains/subscription/catalog/selectedSubscriptionStorage';
import { clearBookMealData } from '../../data/domains/orders/cartStorage';
import { clearCheckoutSlug } from '../../data/domains/orders/checkoutSlugStorage';

const CUSTOMER_ASYNC_KEYS = ['journeyData'] as const;
const CUSTOMER_ASYNC_PREFIXES = ['pending_meal_selection_', 'dashboard_macro_'] as const;

async function clearCustomerAsyncStorageKeys(): Promise<void> {
  try {
    const keys = await AsyncStorage.getAllKeys();
    const to_remove = keys.filter(
      key =>
        (CUSTOMER_ASYNC_KEYS as readonly string[]).includes(key) ||
        CUSTOMER_ASYNC_PREFIXES.some(prefix => key.startsWith(prefix)),
    );
    if (to_remove.length > 0) {
      await AsyncStorage.multiRemove(to_remove);
    }
  } catch {
    // ignore
  }
}

/** Call on logout / 401 / API-target switch before the next user sees shared screens. */
export async function clearCustomerSessionCaches(): Promise<void> {
  useClassicMenuStore.getState().resetSession();
  useFoodMenuStore.getState().resetSession();
  resetBootMenuPrefetchGate();

  await Promise.all([
    clearSelectedSubscription(),
    clearBookMealData(),
    clearCheckoutSlug(),
    clearCustomerAsyncStorageKeys(),
  ]);
}
